import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { type FieldValues, useForm, useWatch } from 'react-hook-form';
import { generateZodSchema } from '../compiler';
import type { FormFieldSchema } from '../types';
import { PreviewDebugTabs, PreviewEmptyState, PreviewHeader, PreviewInputPanel } from './index';

interface FormPreviewProps {
  fields: FormFieldSchema[];
  onUploadFile?: (file: File, category?: string) => Promise<number>;
  onGetFile?: (fileId: number) => Promise<{ fileName: string; fileType: string; viewUrl?: string }>;
}

export function FormPreview({
  fields,
  onUploadFile: propOnUploadFile,
  onGetFile: propOnGetFile,
}: FormPreviewProps) {
  // Store mock file metadata for the preview session
  const [mockFiles, setMockFiles] = React.useState<
    Record<number, { fileName: string; fileType: string; viewUrl: string }>
  >({});

  const onUploadFile = React.useCallback(
    async (file: File, category?: string) => {
      if (propOnUploadFile) return propOnUploadFile(file, category);

      // Mock implementation for preview
      const fileId = Math.floor(Math.random() * 1000000);
      const viewUrl = URL.createObjectURL(file);
      setMockFiles((prev) => ({
        ...prev,
        [fileId]: { fileName: file.name, fileType: file.type, viewUrl },
      }));
      return fileId;
    },
    [propOnUploadFile]
  );

  const onGetFile = React.useCallback(
    async (fileId: number) => {
      if (propOnGetFile) return propOnGetFile(fileId);

      const mock = mockFiles[fileId];
      if (mock) return mock;

      throw new Error('File not found');
    },
    [propOnGetFile, mockFiles]
  );

  const validationSchema = React.useMemo(() => generateZodSchema(fields), [fields]);

  const {
    control,
    reset,
    formState: { errors, isValid, isDirty, touchedFields },
  } = useForm<FieldValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: fields.reduce((acc, field) => {
      const key = field.name || field.id;
      const fAny = field as unknown as Record<string, unknown>;
      acc[key] = fAny.defaultValue || (field.type === 'checkbox' ? false : '');
      return acc;
    }, {} as FieldValues),
  });

  // Keep form state in sync with dynamic field additions/removals. Tracked via a ref (not
  // state) since it's only bookkeeping for the comparison below, not something that should
  // itself trigger a re-render.
  const prevFieldsJsonRef = React.useRef(JSON.stringify(fields));

  React.useEffect(() => {
    const currentFieldsJson = JSON.stringify(fields);
    if (currentFieldsJson !== prevFieldsJsonRef.current) {
      reset(
        fields.reduce((acc, field) => {
          const key = field.name || field.id;
          const fAny = field as unknown as Record<string, unknown>;
          acc[key] = fAny.defaultValue || (field.type === 'checkbox' ? false : '');
          return acc;
        }, {} as FieldValues)
      );
      prevFieldsJsonRef.current = currentFieldsJson;
    }
  }, [fields, reset]);

  const formValues = useWatch({ control });

  return (
    <div className="flex flex-col h-full bg-slate-50/50 dark:bg-slate-950/50 overflow-hidden">
      <PreviewHeader isValid={isValid} />

      {fields.length === 0 ? (
        <PreviewEmptyState />
      ) : (
        <div className="flex-1 flex grid grid-cols-1 md:grid-cols-2 divide-x overflow-hidden">
          <PreviewInputPanel
            fields={fields}
            control={control}
            isValid={isValid}
            formValues={formValues}
            onUploadFile={onUploadFile}
            onGetFile={onGetFile}
          />

          <PreviewDebugTabs
            fields={fields}
            formValues={formValues}
            isValid={isValid}
            isDirty={isDirty}
            errors={errors}
            touchedFields={touchedFields}
            onGetFile={onGetFile}
          />
        </div>
      )}
    </div>
  );
}
